import React, { useState, useCallback } from 'react';
import {
  DragDropContext,
  DropResult,
  DragStart,
  DragUpdate
} from '@hello-pangea/dnd';
import { 
  Filter, 
  Search, 
  Grid, 
  List
} from 'lucide-react';
import { useSharedCart } from '@/components/shared-cart/SharedCartProvider';
import { useUserStore } from '@/stores/userStore';
import { useKanban } from '@/hooks/useKanban';
import { KanbanProductCard } from './KanbanProductCard';
import { KanbanColumn } from './KanbanColumn';

interface KanbanBoardProps {
  sessionId: string;
  items: any[];
  participants: any[];
  className?: string;
  currentUserId?: string;
}


export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  sessionId,
  items: _items,
  participants,
  className = '',
  currentUserId,
}) => {
  const { sendMessage, isConnected } = useSharedCart();
  const { 
    columns, 
    loading, 
    error, 
    refreshBoard, 
    moveItem
  } = useKanban(sessionId);
  
  const [boardView, setBoardView] = useState<'board' | 'list'>('board');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters] = useState<string[]>([]);
  const [isDragDisabled] = useState(false);
  
  // Drag and drop state
  const [, setDraggedItem] = useState<any | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  
  // Handle drag start
  const handleDragStart = useCallback((start: DragStart) => {
    const draggedId = start.draggableId;
    const sourceColumn = columns.find(col => 
      col.items && col.items.some((item: any) => item.id === draggedId)
    );
    const draggedItem = sourceColumn?.items?.find((item: any) => item.id === draggedId);
    
    setDraggedItem(draggedItem);
    
    // Send drag start message to other participants
    if (isConnected && draggedItem) {
      sendMessage('kanban_drag_start', {
        itemId: draggedId,
        sourceColumnId: start.source.droppableId,
        productId: draggedItem.product?.id,
        draggedBy: useUserStore.getState().user?.id || 'guest',
      });
    }
  }, [columns, isConnected, sendMessage]);
  
  // Handle drag update
  const handleDragUpdate = useCallback((update: DragUpdate) => {
    const newOverColumn = update.destination?.droppableId || null;
    setDragOverColumn(newOverColumn);
    
    // Send drag update for real-time cursors
    if (isConnected && update.destination) {
      sendMessage('kanban_drag_update', {
        itemId: update.draggableId,
        overColumnId: newOverColumn,
        position: update.destination.index,
      });
    }
  }, [isConnected, sendMessage]);
  
  // Handle drag end
  const handleDragEnd = useCallback(async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    
    // Reset drag state
    setDraggedItem(null);
    setDragOverColumn(null);
    
    // If no destination, return
    if (!destination) return;
    
    // If dropped in same position, return
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    try {
      // Use the hook's moveItem function
      await moveItem({
        itemId: draggableId,
        columnId: destination.droppableId,
        position: destination.index,
        isGuest: false, // This would come from user context
      });
    } catch (error) {
      console.error('Failed to move item:', error);
      // Optionally show error message to user
    }
  }, [moveItem]);
  
  // Filter items based on search and filters
  const filteredColumns = columns.map(column => ({
    ...column,
    items: (column.items || []).filter((item: any) => {
      const matchesSearch = searchQuery === '' || 
        item.product?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.notes?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesFilters = selectedFilters.length === 0 || 
        selectedFilters.some(() => {
          // Add filter logic here (e.g., by category, price range, etc.)
          return true;
        });
      
      return matchesSearch && matchesFilters;
    }),
  }));
  
  // Calculate board statistics
  const totalProducts = columns.reduce((sum, col) => sum + (col.items?.length || 0), 0);
  const lovedProducts = columns.find(col => col.title === 'Love It')?.items?.length || 0;
  
  // Loading state
  if (loading) {
    return (
      <div className={`kanban-board ${className} flex items-center justify-center p-8`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading kanban board...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`kanban-board ${className} flex items-center justify-center p-8`}>
        <div className="text-center">
          <div className="text-red-500 mb-2">⚠️ Error Loading Board</div>
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={refreshBoard}
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`kanban-board ${className}`}>
      {/* Board Header */}
      <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Product Decision Board</h2>
            <p className="text-sm text-gray-600">
              {totalProducts} products • {lovedProducts} loved • {participants.length} participants
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* View Toggle */}
          <div className="flex bg-gray-200 rounded-lg p-1">
            <button
              onClick={() => setBoardView('board')}
              className={`p-2 rounded text-sm transition-colors ${
                boardView === 'board' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setBoardView('list')}
              className={`p-2 rounded text-sm transition-colors ${
                boardView === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          
          {/* Filters */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg transition-colors ${
              showFilters ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Filters Panel */}
      {showFilters && (
        <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">Filter by:</span>
            {/* Add filter options here */}
            <button className="px-3 py-1 text-xs border border-gray-300 rounded-full hover:bg-gray-50">
              Price Range
            </button>
            <button className="px-3 py-1 text-xs border border-gray-300 rounded-full hover:bg-gray-50">
              Category
            </button>
            <button className="px-3 py-1 text-xs border border-gray-300 rounded-full hover:bg-gray-50">
              Added By
            </button>
          </div>
        </div>
      )}
      
      {/* Connection Status */}
      {!isConnected && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            ⚠️ Not connected - Changes won't be synchronized with other participants
          </p>
        </div>
      )}
      
      {/* Board Content */}
      {boardView === 'board' ? (
        <DragDropContext
          onDragStart={handleDragStart}
          onDragUpdate={handleDragUpdate}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-6 overflow-x-auto pb-6">
            {filteredColumns.map((column) => (
              <KanbanColumn
                key={column.id}
                column={column}
                participants={participants}
                dragOverColumn={dragOverColumn}
                isDragDisabled={isDragDisabled}
                cartId={sessionId}
                currentUserId={currentUserId}
              />
            ))}
          </div>
        </DragDropContext>
      ) : (
        <div className="space-y-4">
          {filteredColumns.map((column) => (
            <div key={column.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: column.color }}
                />
                <h3 className="font-medium text-gray-900">{column.title}</h3>
                <span className="text-sm text-gray-500">({column.items.length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {column.items.map((item: any) => (
                  <KanbanProductCard
                    key={item.id}
                    item={item}
                    participants={participants}
                    isDragDisabled={true}
                    columnColor={column.color}
                    cartId={sessionId}
                    currentUserId={currentUserId}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Empty State */}
      {totalProducts === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Grid className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Yet</h3>
          <p className="text-sm">
            Add products to your cart to start organizing them on the decision board.
          </p>
        </div>
      )}
    </div>
  );
};